import { FormControlLabel, Switch } from '@material-ui/core'
import LaunchIcon from '@material-ui/icons/Launch'
import * as Common from '@tfo/common'
import { Elements, InfoButton, PhoneNumberInput, TextButton, TextInput, utils } from '@tfo/common'
import { CheckpointType, NationalIdOrBirthDateType } from '@tfo/common/dist/models'
import { RadioButtonData } from '@tfo/common/src/components/radio-button-group/RadioButtonGroup'
import { observer } from 'mobx-react'
import personnummer from 'personnummer.js'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import Validator from 'validatorjs'
import DuplicateOrderDetectedDialog from '../../../../components/modals/duplicate-order-detected-modal/DuplicateOrderDetectedDialog'
import InvalidEmailDetectedDialog from '../../../../components/modals/invalid-email-detected-modal/InvalidEmailDetectedDialog'
import OrderConfirmationModal from '../../../../components/modals/order-confirmation-modal/OrderConfirmationModal'
import { Upload } from '../../../../components/UploadFile'
import RootStore from '../../../../stores/RootStore'
import { showTemporaryMessageOrderFormShareReport, showTemporaryMessageOrderFormSubmit } from '../../../../utils/Utils'
import Form from '../../common/form/Form'
import { SelectCountries } from '../../common/SelectCountries'
import { SelectOrderRecipients } from '../../common/SelectOrderRecipients'
import DisplayConfiguredOrderRecipients from './DisplayConfiguredOrderRecipients'
import './NewReportOrder.scss'


const { Heading1, Heading4, Heading7, TextArea, Paragraph, Anchor, InputErrorMessage, } = Elements
export interface NewReportOrderFormProps {
	rootStore?: RootStore
}

interface IndividualInformationFromExternalSource {
	ExternalOrderId: string
	Individual: {
		Email?: string
		FirstName?: string
		LastName?: string
		MobilePhoneNumber?: string
		NationalId?: string
	}
}

export type IndividualExistForm = {
	companyId: number
	email: string
	nationalId: string
}

Validator.register('nationalIdNoInput', (value: string | number | boolean) => {
	if (typeof value !== "string") {
		return false;
	}

	const parsedValue = personnummer.parse(value)
	const isValidNationalId = personnummer.validate(parsedValue.normalised)
	return isValidNationalId && parsedValue.age >= 18 && parsedValue.age < 110

}, "FORMAT")

Validator.register('birthDateAge', (value: string | number | boolean) => {
	if (typeof value !== "string") {
		return false;
	}

	const date = buildDateFromKnownGroupRegExp(value, /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})/)
	return date
		? validateAgeOlderThan18(date) && validateAgeYoungerThan110(date)
		: false;
}, "FORMAT") //error message is not used.


const buildDateFromKnownGroupRegExp = (value: string, dateExp: RegExp): Date => {
	const match = dateExp.exec(value)
	if (!match || !match.groups) {
		return null;
	}

	const groups = match.groups;
	return new Date(Number(groups['year']), Number(groups['month']) - 1, Number(groups['day']))
}

const validateAgeOlderThan18 = (birthDate: Date): boolean => {
	const now = new Date();
	const date18YearsAgo = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());

	return birthDate <= date18YearsAgo;
}

const validateAgeYoungerThan110 = (birthDate: Date): boolean => {
	const now = new Date();
	const date110YearsAgo = new Date(now.getFullYear() - 110, now.getMonth(), now.getDate());

	return birthDate > date110YearsAgo;
}

const NewReportOrderForm = observer((props) => {
	const [submitClicked, setSubmitClicked] = React.useState<boolean>(false)
	const [disabled, setSubmitDisabled] = React.useState<boolean>(false)
	const [needReportRecipientError, setNeedReportRecipientError] = React.useState<boolean>(false)
	const [data, setData] = React.useState<Common.models.NewOrderData>()
	const [invoiceReferencePerson, setInvoiceReferencePerson] = React.useState<Common.interfaces.CompanyInvoiceRuleModel>({ key: '', defaultValue: '', description: '', error: false, mandatory: false, label: '', activated: false })
	const [customerSpecificInvoiceRules, setCustomerSpecificInvoiceRules] = React.useState<Array<Common.interfaces.CompanyInvoiceRuleModel>>()
	const [formGroup, setFormGroup] = React.useState<any[]>([])
	const [educationGroup, setEducationGroup] = React.useState<any[]>([])
	const [addedFile, setAddedFile] = React.useState<boolean>(false)
	const [requiredCountries, setRequiredCountries] = React.useState<boolean>(false)
	const [companyAddress, setCompanyAddress] = React.useState<Common.models.ICostCenter>(null)
	const clientViewStore = props.clientViewStore
	const company = props.company as Common.models.ICompany
	const needReportRecipient = props.needReportRecipient
	const orderRecipientIds = props.orderRecipientIds
	const history = useHistory()
	const intl = useIntl()
	const [characterCount, setCharacterCount] = React.useState(0);
	const maxCharacters = 150
	const maxCharactersCustomerInternalComment = 150
	const maxInputCharachters = 30
	const maxCharactersInvoiceReference = 100
	const isTest: boolean = ((window as any).tfoEnv.includes('test') || window.location.hostname === 'localhost')
	const [confirmDuplicateOrderModal, setConfirmDuplicateOrderModal] = React.useState<boolean>(false)
	const [invalidEmailModal, setInvalidEmailModal] = React.useState<boolean>(false)
	const [orderConfirmationModal, setOrderConfirmationModal] = React.useState<boolean>(false)
	const [nationalIdInput, setNationalIdInput] = React.useState<NationalIdOrBirthDateType | null >(null)
	const [isExternalOrder, setIsExternalOrder] = React.useState<boolean>(false)
	const [hasCountryError, setHasCountryError] = React.useState<boolean>(false)
	const [files, setFiles] = React.useState<File[]>([])
	const [ljungSjobergAddon, setLjungSjobergAddon] = React.useState<boolean>(props.reportPackageAddons.some(addon => addon.partnerName === "Ljung & Sjöberg"));
	const [phonenumberRequired, setPhonenumberRequired] = React.useState<boolean>(false)
	const [isCommentFocused, setIsCommentFocused] = React.useState<boolean>(false)
	
	const checkCountryError = (hasError: boolean): void => {
		setHasCountryError(hasError)
	}

	const toggleConfirmDuplicateOrderModal = (): void => {
		setConfirmDuplicateOrderModal(!confirmDuplicateOrderModal)
	}

	const toggleInvalidEmailModal = (): void => {
		setInvalidEmailModal(!invalidEmailModal)
	}

	const toggleOrderConfirmationModal = (): void => {
		setOrderConfirmationModal(!orderConfirmationModal)
	}

	//Creation of Validator placed here to access user email in clientViewStore
	Validator.register('emailInputNotSameAsCustomer', (value: string | number | boolean) => {
		if (typeof value !== "string") {
			return false;
		}
		const email = clientViewStore.rootStore.userStore.user.email
		return value !== email

	}, "FORMAT")

	React.useEffect(() => {
		const individualInformation = window.sessionStorage.getItem('individualInformation')
		const parsedIndividualInformation = JSON.parse(individualInformation)
		setCompanyAddress(company.costCenters.map((cc) => { return cc })[0])

		initInternalFormData(parsedIndividualInformation)

		if (parsedIndividualInformation && parsedIndividualInformation.ExternalOrderId) {
			clientViewStore.externalAtsReference = parsedIndividualInformation.ExternalOrderId
			setIsExternalOrder(true)
		}

		clientViewStore.resetNewOrder()
		setData(prev => {
			return {
				...prev,
				optionalCheckpoints: clientViewStore.selectedReportPackage.optionalCheckpoints.map((o) => { return { checkpointId: o.id, isIncluded: false, checkpointType: o.checkpointType } })
			}
		})
		invoiceRules()
	}, [])
	
	React.useEffect(() => {
		const emailVal = data?.email?.value
		const nationalIdVal = data?.nationalId?.value
		const individualExistForm: IndividualExistForm = { companyId: company.id, email: "", nationalId: "" }

		const validEmail = emailVal && !new Validator({'email': emailVal}, {'email': data.email.rules}).fails()

		const validNationalId = nationalIdVal && !new Validator({'nationalId': nationalIdVal}, {'nationalId': data.nationalId.rules}).fails()

		const formChanged =
			individualExistForm.email !== emailVal ||
			individualExistForm.nationalId !== nationalIdVal

		const checkIndividual = setTimeout(() => {
			if (data?.email && data?.nationalId) {
				if (formChanged && (validEmail && validNationalId)) {
					individualExistForm.email = emailVal.toString()
					individualExistForm.nationalId = nationalIdVal
					const { companyId, email, nationalId } = individualExistForm

					clientViewStore.checkIfIndividualExists(email, companyId, nationalId)
				}
			} else {
				if (formChanged && validEmail) {
					individualExistForm.email = emailVal.toString()
					const { companyId, email } = individualExistForm
					clientViewStore.checkIfIndividualExists(email, companyId, null)
				}
			}
		}, 500)

		return () => clearTimeout(checkIndividual)
	}, [data?.email?.value, data?.nationalId?.value])
	
	const initInternalFormData = (parsedIndividualInformation: IndividualInformationFromExternalSource) => {
		const data: Common.models.NewOrderData = new Common.models.NewOrderData()

		const hasNationalId = !!parsedIndividualInformation?.Individual?.NationalId;
		const parsedNationalId = hasNationalId
			? personnummer.parse(parsedIndividualInformation.Individual.NationalId).normalised
			: null

		if (clientViewStore.selectedReportPackage.requirePersonalIdentification) {
			if(hasBasicCheckpoints()) {
				data.nationalId = {
					fieldId: 'field3',
					id: 'nationalId',
					rules: hasBasicCheckpoints()
						? ['required', 'regex:/([12]\\d{3})(0\\d|1[0-2])(\\d{6,6})$/', 'nationalIdNoInput']
						: [],
					pattern: '\\d*',
					type: 'text',
					value: parsedNationalId || '',
					hasError: false,
				} 
				setNationalIdInput(data.nationalId)
			} else {
				data.birthDate = {
					fieldId: 'field5',
					id: 'birthDate',
					rules: hasBasicCheckpoints()
						? []
						: ['required', 'regex:/^([12]\\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01]))$/', 'birthDateAge'],
					type: 'string',
					pattern: '\\d*',
					value: parsedIndividualInformation?.Individual.NationalId || '',
					hasError: false
				}
				setNationalIdInput(data.birthDate)
			}
		}
		
		data.firstName = {
			fieldId: 'field1', // Stupid naming to avoid auto-complete in Chrome
			id: 'firstName',
			rules: 'required|min:1',
			type: 'text',
			value: parsedIndividualInformation?.Individual.FirstName || ''
		}
		data.lastName = {
			fieldId: 'field2',
			id: 'lastName',
			rules: 'required|min:1',
			type: 'text',
			value: parsedIndividualInformation?.Individual.LastName || ''
		}

		data.email = {
			fieldId: 'field4',
			id: 'email',
			rules: !isTest ? 'required|email|emailInputNotSameAsCustomer' : 'required|email',
			type: 'email',
			value: parsedIndividualInformation?.Individual.Email || '',
		}
		
		data.additionalOrderRecipientUserId = { value: NaN }
		if (needReportRecipient) {
			if (orderRecipientIds) {
				data.additionalOrderRecipientUserId.value = orderRecipientIds[0]
			}
		}
		
		setFormGroup([
			data.firstName,
			data.lastName,
			data.email,
		])
		
		data.phoneNumber = {
			fieldId: 'field6',
			id: 'phoneNumber',
			rules: ljungSjobergAddon ? 'required' : '',
			value: parsedIndividualInformation?.Individual.MobilePhoneNumber || '',
			hasError: false
		}

		data.comments = { value: '' }
		data.customerInternalComment = { value: ''}

		const addresses: RadioButtonData[] = []
		if (company.costCenters.length > 0) {
			company.costCenters.forEach(
				(cc: any) => {
					addresses.push({
						label: [
							cc.showCompanyName &&
							company.name,
							cc.address,
							cc.postalCode,
							cc.postalArea,
							cc.email,
							cc.costCenterText,
							cc.contactPersonCustomer,
						]
							.filter(Boolean)
							.join(', '),
						hasAttachment: false,
						attachmentValue: null,
						attachmentLabel: null,
						addressId: cc.id
					})
				},
			)
		}
		else if (company.costCenters.length <= 0) {
			setSubmitDisabled(() => true)
		}

		data.address = {
			selected: 0,
			items: addresses,
		}

		data.invoiceReference = { value: '' }
		data.optionalCheckpoints = []
		data.fileIDs = []
		data.countriesBackgroundCheck = []
		data.criminalRecords = [{ id: '', region: '', country: '', isCommonCountry: false }]

		const hasHighestEducation = clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.HighestEducation,
		)
		const hasEmployment = clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.Employment,
		)
		if (hasHighestEducation && !hasEmployment) {
			data.educationinstitute = {
				fieldId: 'field10',
				id: 'educationinstitute',
				rules: 'required|min:1',
				type: 'text',
			}
			data.educationdegree = {
				fieldId: 'field11',
				id: 'educationdegree',
				rules: 'required|min:1',
				type: 'text',
			}
			data.educationfromdate = {
				fieldId: 'field12',
				id: 'educationfromdate',
				rules: ['regex:/(^\\d{4}$|^\\d{4}-\\d{2}$)/', 'max:10'], //YYYY or YYYY-MM
				type: 'text',
			}
			data.educationtodate = {
				fieldId: 'field13',
				id: 'educationtodate',
				rules: ['regex:/(^\\d{4}$|^\\d{4}-\\d{2}$)/', 'max:10'], //YYYY or YYYY-MM
				type: 'text',
			}
			setEducationGroup([
				data.educationinstitute,
				data.educationdegree,
				data.educationfromdate,
				data.educationtodate,
			])
		}
		setData(data)
	}

	const applyValue = (internalId: string, value: any) => {
		if (data[internalId]) {
			data[internalId].value = value
			setFormGroup(prev => [...prev])
		}
		if (submitClicked) {
			validateFields()
			setAddedFile(validateFiles(files, resumeinPackage()))
			setRequiredCountries(validateCountries(
				data.countriesBackgroundCheck,
				countriesBackgroundCheck()))
		}
	}
	
	const onChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
		const internalId = internalIdAttribute(event)
		if (internalId === 'nationalId') {
			event.currentTarget.value = event.currentTarget.value.replace(/\s/g, '')
		}

		applyValue(internalId, event.currentTarget.value)
	}
	
	const internalIdAttribute = (event: React.FormEvent<HTMLInputElement>) => {
		const internalIdAttr = event.currentTarget.attributes.getNamedItem(
			'data-internal-id',
		)
		const internalId = internalIdAttr ? internalIdAttr.value : ''

		return internalId
	}

	const invoiceRules = () => {
		if (clientViewStore.invoiceReferencePersonRule) {
			const invoiceReferenceValue = clientViewStore.invoiceReferencePersonRule;
			setInvoiceReferencePerson({ ...invoiceReferenceValue, description: invoiceReferenceValue.description ?? '', defaultValue: invoiceReferenceValue.defaultValue ?? '', error: false })
		}

		setCustomerSpecificInvoiceRules(clientViewStore.companySpecificInvoicingRules.length > 0 ? clientViewStore.companySpecificInvoicingRules.map((rule) => { return { ...rule, error: false } }) : [])
	}

	const resumeinPackage = () => {
		return clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.Employment) || clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.InternationalResume)
	}

	const hasBasicCheckpoints = () => {
		return clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.NameAndNationalId)
	}

	const validateFields = () => {
		const dataToValidate = {}
		const withRules = {}
		Object.entries(data)
			// phone number is not validated by Validator, so exclude it 
			// but rather the <PhoneNumberInput /> indicates whether the value is probably a phone number.
			.filter(([objectKey, _]) => objectKey !== 'phoneNumber')
			.forEach(([objectKey, objectValue]) => {
				objectValue.hasError = false //Reset state to valid, before validating
				if (!objectValue.rules) {
					return
				}
				dataToValidate[objectKey] = objectValue.value
				withRules[objectKey] = objectValue.rules
			})
	
		if (data.phoneNumber.rules.includes("required") && !data.phoneNumber.value) {
			data.phoneNumber.hasError = true;
			setPhonenumberRequired(true);
		}
		
		const validator = new Validator(dataToValidate, withRules)
		if (validator.fails()) {
			Object.keys(validator.errors.all()).forEach(objectKey => {
				data[objectKey].hasError = true
			})
			return false
		}

		//include phone numbers validation here instead.
		return validator.passes() && !data.phoneNumber.hasError
	}

	const validateFiles = (typeOfArray: File[], checkpointIncluded: boolean) => {
		if (checkpointIncluded) {
			if (typeOfArray.length > 0) {
				return true
			} else {
				return false
			}
		} else {
			return true
		}
	}

	const validateCountries = (
		countries: {
			id: string
			region: string
			country: string
			isCommonCountry: boolean
		}[],
		checkpointIncluded: boolean
	) => {
		if (checkpointIncluded) {
			setHasCountryError(countries.length < 1)
			return countries.length > 0
		} else {
			return true
		}
	}

	const onSubmitClick = () => {
		toggleOrderConfirmationModal()
		onSubmit()
	}

	const sendMessageToExternalIframe = () => {
		const message = JSON.stringify({
			message: 'tfo.orderComplete',
		})
		// Target origin set to *, because we don't have the url to the parent component provider
		window.parent.postMessage(message, '*')
	}

	const endAtsSession = (): void => {
		window.sessionStorage.removeItem('externalHostedOrderFlow')
		window.sessionStorage.removeItem('individualInformation')
		Common.utils.deleteCookie()
	}

	const onSubmit = async () => {
		setConfirmDuplicateOrderModal(false)
		setInvalidEmailModal(false)
		setSubmitClicked(true)
		setSubmitDisabled(true)
		const countriesRequired = validateCountries(
			data.countriesBackgroundCheck,
			countriesBackgroundCheck())
		const allFormValidations = [
			validateFields(),
			validateFiles(files, resumeinPackage()),
			countriesRequired,
		]

		if (clientViewStore.invoiceConfiguration.shouldInvoice) {
			const hasInvoiceReferencePersonRule: boolean = clientViewStore.invoiceReferencePersonRule !== null
			if (hasInvoiceReferencePersonRule) {

				const ruleIsMandatory: boolean = clientViewStore.invoiceReferencePersonRule.mandatory

				if (ruleIsMandatory) {
					const invoiceReferenceValueIsValid: boolean = invoiceReferencePerson.defaultValue !== ""
					invoiceReferencePerson.error = !invoiceReferenceValueIsValid
					allFormValidations.push(invoiceReferenceValueIsValid)
				}
			}

			const companySpecificRulesToValidate: Array<Common.interfaces.CompanyInvoiceRuleModel> = customerSpecificInvoiceRules.filter(rule => rule.mandatory);

			if (companySpecificRulesToValidate.length > 0) {
				const hasValidValues: boolean = companySpecificRulesToValidate.some(rule => rule.defaultValue !== "")
				setCustomerSpecificInvoiceRules(customerSpecificInvoiceRules.reduce((previousValue, currentValue) => {
					const hasError: boolean = currentValue.mandatory && currentValue.defaultValue === ""
					const updatedvalue: Common.interfaces.CompanyInvoiceRuleModel = { ...currentValue, error: hasError }
					previousValue.push(updatedvalue)
					return previousValue
				}, []))
				allFormValidations.push(hasValidValues)
			}
		}

		if (needReportRecipient) {
			const needReportRecipientError = isNaN(data.additionalOrderRecipientUserId.value)
			allFormValidations.push(!needReportRecipientError)
			setNeedReportRecipientError(needReportRecipientError)
		}

		const validation = allFormValidations.every(value => value === true)

		if (validation) {
			const submittedCustomerSpecificRules: Array<Common.interfaces.OrderInvoiceRule> = customerSpecificInvoiceRules.map((rule) => { return { key: rule.key, value: rule.defaultValue } })
			const invoiceReferencePersonValue: string = invoiceReferencePerson.defaultValue

			await clientViewStore.createNewOrder(data, company.id, invoiceReferencePersonValue, submittedCustomerSpecificRules, files)
			const failedToCreateOrder = clientViewStore.createServerError
				|| clientViewStore.invalidNationalId
				|| clientViewStore.invalidEmail
				|| clientViewStore.duplicateOrder

			if (!failedToCreateOrder) {
				if (clientViewStore.externalHostedOrderFlow) {
					sendMessageToExternalIframe()
					endAtsSession()
					// We don't use history.push because it doesn't happen instantly and we get redirected to the login page instead
					window.location.href = `/ats-confirmed-order`
				} else {
					history.push(`/new-report-done/${company.id}`)
				}
			} else {
				if (clientViewStore.duplicateOrder) {
					setConfirmDuplicateOrderModal(true)
				}
				else if (clientViewStore.invalidEmail) {
					setInvalidEmailModal(true)
				}
				else {
					setSubmitDisabled(false)
				}
			}
		} else {
			/* tslint:disable */
			setTimeout(() => {
				const firstWithError: HTMLElement | null = document.querySelector(
					'*[class*="--hasError"]',
				)
				firstWithError &&
					firstWithError.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)
		}
		setSubmitDisabled(false)
	}

	const onChangeOptionalCheckpoint = (checkpointId: number, isIncluded: boolean, checkpointType: string) => {
		if (
			data.optionalCheckpoints.some(
				option => option.checkpointId === checkpointId,
			)
		) {
			data.optionalCheckpoints.find(
				option => option.checkpointId === checkpointId,
			).isIncluded = isIncluded
		} else {
			data.optionalCheckpoints.push({
				checkpointId: checkpointId,
				isIncluded: isIncluded,
				checkpointType: checkpointType
			})
		}
	}

	const addFile = (file: File) => {
		files.push(file)
		setFiles(files)
		setAddedFile(validateFiles(
			files,
			resumeinPackage(),
		))
	}

	const removeFile = (file: File) => {

		if (!files.some(dataFile => dataFile.name === file.name)) {
			return
		}

		files.splice(files.indexOf(file), 1)
		setFiles(files)
		setAddedFile(validateFiles(
			files,
			resumeinPackage(),
		))
	}

	const onChangeCompanySpecificInvoicingRules = (e: React.FormEvent<HTMLInputElement>) => {
		const { name, value } = e.currentTarget;
		const updatedValue = customerSpecificInvoiceRules.find(rule => rule.key === name)
		const index = customerSpecificInvoiceRules.indexOf(updatedValue)
		const hasError = (value.length < 1 && updatedValue.mandatory)
		customerSpecificInvoiceRules[index] = { ...updatedValue, defaultValue: value, error: hasError }
		setCustomerSpecificInvoiceRules(() => [...customerSpecificInvoiceRules])
	}

	const onChangeInvoiceReferencePerson = (e: React.FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget;
		const hasError = (value.length < 1 && invoiceReferencePerson.mandatory)
		setInvoiceReferencePerson({ ...invoiceReferencePerson, defaultValue: value, error: hasError })
	}

	const onSetCharacterCount = (e) => {
		onChangeHandler(e)
		setCharacterCount(() => e.target.value.length)
	}

	const hasEducationPackage = (): boolean => {
		const hasHighestEducation = clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.HighestEducation,
		)
		const hasEmployment = clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.Employment,
		)
		return hasHighestEducation && !hasEmployment
	}

	const countriesBackgroundCheck = (): boolean => {
		return clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.Countries
		)
	}

	const criminalRecords = (): boolean => {
		return clientViewStore.selectedReportPackage.checkpoints.includes(
			CheckpointType.CriminalRecords
		)
	}

	const getFormattedErrorMessages = (group: Array<any>) => {
		return submitClicked
			? group
				.concat([data.phoneNumber])
				.filter(item => item.hasError)
				.map(item =>
					intl.formatMessage({
						id: `errormessage.${item.id.toLocaleLowerCase()}`,
					}),
				)
			: []
	}

	const setPhonenumberError = (hasError: boolean): void => {
		data.phoneNumber.hasError = hasError
	}

	return (
		<>
			{data &&
				<React.Fragment>
					<Form
						noValidate
						autoComplete={'off'}
						onSubmit={(e: React.FormEvent) => {
							e.stopPropagation()
							e.preventDefault()
						}}>
						<Elements.Label>
							{intl.formatMessage({ id: 'common.typeofreport' })}
						</Elements.Label>

						<div className={'NewReportOrder__BlockSpace--small'}>
							<Heading1>
								{clientViewStore.selectedReportPackage.customName}{' '}
								{!countriesBackgroundCheck() && (
									<InfoButton
										onClick={() => (clientViewStore.modalOpen = true)}
									/>
								)}
							</Heading1>
						</div>

						<div className={'NewReportOrder__BlockSpace--big'}>
							<Heading4>
								{intl.formatMessage({ id: 'neworder.individualInfo' })}
							</Heading4>
							<Paragraph small>
								{intl.formatMessage({id: 'neworder.individualInfo.subtitle'})}
							</Paragraph>
						</div>

						<div className={'NewReportOrder__BlockSpace--small'}>
							{clientViewStore.selectedReportPackage.requirePersonalIdentification &&
								<div>
									<TextInput
										key={nationalIdInput.id}
										id={nationalIdInput.fieldId}
										data-internal-id={nationalIdInput.id}
										value={
											nationalIdInput.value || ''}
										hasError={nationalIdInput.hasError}
										name={nationalIdInput.id}
										type={nationalIdInput.type}
										pattern={nationalIdInput.pattern}
										placeholder={intl.formatMessage({
											id: `neworder.${nationalIdInput.id.toLocaleLowerCase()}.placeholder`,
										})}
										onChange={e => onChangeHandler(e)}
										style={{width: '99%'}}

									/>

									<div className={'ExistingNationalId'}>
										{nationalIdInput.hasError &&
											<InputErrorMessage>
												{intl.formatMessage({id: `errormessage.${nationalIdInput.id.toLocaleLowerCase()}`})}
											</InputErrorMessage>}
									</div>
								</div>}

							<div className={'InputGroup NewReportOrder__BlockSpace--small'}>
								{formGroup.map(data => {

									return (
										<>
											<TextInput
												key={data.id}
												id={data.fieldId}
												data-internal-id={data.id}
												value={data.value || ''}
												disabled={isExternalOrder ? true : false}
												hasError={data.hasError}
												name={data.id}
												type={data.type}
												pattern={data.pattern}
												placeholder={intl.formatMessage({
													id: `neworder.${data.id.toLocaleLowerCase()}.placeholder`,
												})}
												onChange={e => onChangeHandler(e)}
												style={{marginBottom: '8px', width: '98%'}}
											/>
										</>
									)
								})}
								<PhoneNumberInput
									initialValue={data.phoneNumber.value || ''}
									initialCountry={'SE'}
									onBlur={(value, isValid) => {
										applyValue('phoneNumber', value)
										data.phoneNumber.hasError = isValid
									}}
									setPhonenumberError={setPhonenumberError}
									placeholder={intl.formatMessage({
										id: `common.phonenumber.mobile`,
									})}
									required={phonenumberRequired}
								/>
							</div>
							
								{clientViewStore.isExistingIndividual &&
									<div className={'ExistingNationalId'}>
										<p className={'Font'}>
											{intl.formatMessage({id: 'errormessage.existingNationalId'})}
										</p>
									</div>
								}
						</div>
						
						<div className={'NewReportOrder__BlockSpace--small'}>
							{getFormattedErrorMessages(formGroup).map(
								(errorMessage: string, index: number) => (
									<InputErrorMessage key={index}>
										{errorMessage}
									</InputErrorMessage>
								),
							)}
						</div>
						{(clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.Employment) || clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.InternationalResume)) && (
							<>
								<Heading4 className={'NewReportOrder__BlockSpace--big'}>
									{intl.formatMessage({ id: 'checkpoint.resume.title' })}
								</Heading4>
								<Upload
									submitError={submitClicked ? !addedFile : false}
									value={files}
									addFile={(file) =>
										addFile(file)
									}
									removeFile={(file) =>
										removeFile(file)
									}
									companyId={company.id}
									userId={company.accountManagerId}
									network={clientViewStore.network}
								/>
								{data.optionalCheckpoints.map((optionalCheckpoint, index) => (
									<React.Fragment key={`${index}optionalCheckpointfragment`}>
										{(submitClicked ? !addedFile : false) && (
											<InputErrorMessage key={index}>
												{intl.formatMessage({
													id: 'checkpoint.resume.fileUploading.ErrorMessage',
												})}
											</InputErrorMessage>
										)}
										{(clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.CurrentEmployment) ||
											clientViewStore.selectedReportPackage.checkpoints.includes(CheckpointType.InternationalResumeCurrentEmployment)) &&
											<>
												<FormControlLabel
													key={optionalCheckpoint.checkpointId}
													data-cy="optionCheckpointSwitch"
													label={`${intl.formatMessage({ id: 'common.include', })} ${intl.formatMessage({ id: `service.checkpoints.${optionalCheckpoint.checkpointType}`, }).toLowerCase()}`}
													control={
														<Switch
															color="primary"
															value={optionalCheckpoint.isIncluded}
															onChange={e =>
																onChangeOptionalCheckpoint(
																	optionalCheckpoint.checkpointId,
																	e.target.checked,
																	optionalCheckpoint.checkpointType
																)
															}
														/>
													}
												/>
												<Heading7>
													{intl.formatMessage({
														id:
															'checkpoint.currentEmploymentCheckpoint.informationText',
													})}
												</Heading7>
											</>
										}
									</React.Fragment>
								),
								)}
							</>
						)}

						{hasEducationPackage() && (
							<>
								<Heading4 className={'NewReportOrder__BlockSpace--big'}>
									{intl.formatMessage({
										id: 'checkpoint.educationCheckpoint.title',
									})}
								</Heading4>
								<div className={'InputGroup NewReportOrder__BlockSpace--medium'}>
									{educationGroup.map(data => {
										return (
											<TextInput
												key={data.id}
												id={data.fieldId}
												data-internal-id={data.id}
												value={data.value || ''}
												hasError={data.hasError}
												type={data.type}
												pattern={data.pattern}
												placeholder={intl.formatMessage({
													id: `neworder.${data.id.toLocaleLowerCase()}.placeholder`,
												})}
												onChange={e => onChangeHandler(e)}
											/>
										)
									})}
								</div>
								<div className={'NewReportOrder__BlockSpace--small'}>
									{getFormattedErrorMessages(educationGroup).map(
										(errorMessage: string, index: number) => (
											<InputErrorMessage key={index}>
												{errorMessage}
											</InputErrorMessage>
										),
									)}
								</div>
							</>
						)}

						{countriesBackgroundCheck() && (
							<div className={'NewReportOrder__BlockSpace--medium'}>
								<InputErrorMessage >
									{intl.formatMessage({
										id: 'neworder.international.longer.delivery',
									})}
								</InputErrorMessage>

								<div className={'NewReportOrder__BlockSpace--medium'}>
									<Heading4 className={'NewReportOrder__BlockSpace--medium'}>
										{intl.formatMessage({ id: 'neworder.choose.countries' })}
									</Heading4>
									<SelectCountries
										onSelect={newValue =>
											(data.countriesBackgroundCheck = newValue)
										}
										setCountryError={checkCountryError}
										hasError={hasCountryError}
									/>
									<h5>
										{intl.formatMessage({
											id: 'neworder.other.countries.informationtext',
										})}
									</h5>
									{(submitClicked ? hasCountryError : false) && (
										<InputErrorMessage>
											{intl.formatMessage({
												id: 'neworder.other.countries.errortext',
											})}
										</InputErrorMessage>
									)}
								</div>
							</div>
						)}

						{criminalRecords() &&
							<div className={'NewReportOrder__BlockSpace--big'}>
								<Heading4>
									{intl.formatMessage({ id: 'neworder.choose.criminalrecords' })}
								</Heading4>

								<div className={'NewReportOrder__BlockSpace--medium'}>
									<SelectCountries
										onSelect={newValue => data.criminalRecords = newValue}
										hasError={hasCountryError}
										setCountryError={checkCountryError}
									/>
									<h5>{intl.formatMessage({ id: "neworder.other.criminalrecords.informationtext" })}</h5>
								</div>
							</div>}

						<div className={'NewReportOrder__BlockSpace--big'}>
							<Heading4>{intl.formatMessage({id: 'neworder.note'})}</Heading4>
							<Paragraph small>{intl.formatMessage({id: 'neworder.notes.placeholder'}, {b: (chunks) => <strong>{chunks}</strong>})}</Paragraph>
						</div>
						
						<div className={'NewReportOrder__BlockSpace--small'}>
							<TextInput
								data-internal-id={'comments'}
								id={'comments'}
								value={data.comments.value}
								maxLength={maxCharacters}
								onChange={e => onSetCharacterCount(e)}
								onFocus={() => setIsCommentFocused(true)}
								onBlur={() => setIsCommentFocused(false)}
							/>
							<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
								{isCommentFocused && 
								<div>
									<Paragraph small >{intl.formatMessage({id: 'neworder.notes.info'})}</Paragraph>
									<Paragraph small >{intl.formatMessage({id: 'neworder.notes.info.point1'})}</Paragraph>
									<Paragraph small >{intl.formatMessage({id: 'neworder.notes.info.point2'})}</Paragraph>
								</div>}
								<div className="CharacterCount" style={{marginLeft: 'auto'}}>
									<Paragraph className="fontColor">{characterCount + '/' + maxCharacters}</Paragraph>
								</div>
							</div>
						</div>

						<div className={'NewReportOrder__BlockSpace--medium'}>
							<Heading4>{intl.formatMessage({ id: 'neworder.customerInternalComment' })}</Heading4>
							<Paragraph small>{intl.formatMessage({id: 'neworder.customerInternalComment.placeholder'}, {b: (chunks) => <strong>{chunks}</strong>})}</Paragraph>
						</div>

						<div className={'NewReportOrder__BlockSpace--small'}>
							<TextInput
								data-internal-id={'customerInternalComment'}
								id={'customerInternalComment'}
								value={data.customerInternalComment.value}
								maxLength={maxCharactersCustomerInternalComment}
								onChange={e => onChangeHandler(e)}
							/>
							<div className="CharacterCount">
								<Paragraph className="fontColor">{data.customerInternalComment.value.length  + '/' + maxCharactersCustomerInternalComment}</Paragraph>
							</div>
						</div>



						{clientViewStore.companyHasUsersToShareReportWith &&
							<div className={'NewReportOrder__BlockSpace--big'}>
								<Heading4>
									{intl.formatMessage({ id: utils.isOrderer(clientViewStore.rootStore.userStore.user) ? 'neworder.orderRecipient.ordererText' : 'neworder.orderRecipient' })}
								</Heading4>

								<div className={'NewReportOrder__BlockSpace--small'}>
									{utils.isOrderer(clientViewStore.rootStore.userStore.user)
										?
										<>
											{needReportRecipient
												?
												<>
													<SelectOrderRecipients
														defaultId={orderRecipientIds ? orderRecipientIds[0] : undefined}
														error={needReportRecipientError}
														companyId={company.id}
														onSelect={newValue => {
															(data.additionalOrderRecipientUserId.value = newValue ? (newValue.id as number) : NaN)
															setNeedReportRecipientError(false)
														}
														}
													/>
													{needReportRecipientError &&
														<InputErrorMessage>
															{intl.formatMessage({
																id: 'new.report.order.form.need.report.recipient.error',
															})}
														</InputErrorMessage>
													}
												</>
												:
												<DisplayConfiguredOrderRecipients
													userId={clientViewStore.rootStore.userStore.user.id}
													companyId={clientViewStore.rootStore.userStore.user.companyId}
												/>
											}
										</>
										: <>
											{showTemporaryMessageOrderFormShareReport() ? <div style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold', color: 'rgb(236, 157, 201)' }}>
												{intl.formatMessage({ id: 'neworder.summerdelays.sharereport' })}
											</div> : <div style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold', color: 'rgb(236, 157, 201)' }}>
												{intl.formatMessage({ id: 'neworder.orderer.sharereport' })}
											</div>}
											<SelectOrderRecipients
												companyId={company.id}
												onSelect={newValue =>
												(data.additionalOrderRecipientUserId.value = newValue
													? (newValue.id as number)
													: NaN)
												}
											/>
										</>
									}
								</div>
							</div>
						}

						{clientViewStore.invoiceConfiguration.shouldInvoice &&
							<div className={'NewReportOrder__BlockSpace--big'}>
								<Heading4>
									{intl.formatMessage({ id: 'invoice.invoicing' })}
								</Heading4>
							</div>
						}
						<div className="NewReportOrder__BlockSpace--medium">
							{clientViewStore.invoiceConfiguration.shouldInvoice &&
								<>
									{
										(clientViewStore.invoiceReferencePersonRule !== null) &&
										<>
											<Paragraph>
												{intl.formatMessage({ id: 'invoice.recipient' })}
											</Paragraph>
											<Paragraph italic small>
												{invoiceReferencePerson.description}
											</Paragraph>
											<TextInput value={invoiceReferencePerson.defaultValue || ''}
												className="TextInput--withLabel"
												id={'invoiceReferencePerson'}
												key={'invoiceReferencePerson'}
												type="text"
												data-internal-id={'invoiceReferencePerson'}
												onChange={e => onChangeInvoiceReferencePerson(e)}
												hasError={invoiceReferencePerson.error}
												maxLength={maxInputCharachters}
											/>
											<div className="CharacterCount">
												{invoiceReferencePerson.error &&
													<InputErrorMessage>
														{intl.formatMessage({
															id: 'invoice.errorMessage.special.reference.person',
														})}
													</InputErrorMessage>
												}
												<Paragraph className="leftMargin fontColor">{invoiceReferencePerson?.defaultValue?.length ?? 0 + ' / ' + maxInputCharachters}</Paragraph>
											</div>
										</>
									}

									{customerSpecificInvoiceRules.length > 0 &&
										<>
											{
												customerSpecificInvoiceRules.map((rule) => {
													return (
														<div key={rule.key}>
															<Paragraph key={`paragraph.${rule.key}`} >
																{rule.label}
															</Paragraph>
															<Paragraph italic small>
																{rule.description}
															</Paragraph>
															<TextInput value={rule.defaultValue}
																className="TextInput--withLabel"
																id={rule.key}
																key={rule.key}
																type="text"
																name={rule.key}
																onChange={e => onChangeCompanySpecificInvoicingRules(e)}
																maxLength={maxInputCharachters}
																data-internal-id={rule.key}
																hasError={rule.error}
															/>
															<div className="CharacterCount">
																{
																	rule.error &&
																	<InputErrorMessage>
																		{intl.formatMessage({
																			id: 'common.mandatory',
																		})}
																	</InputErrorMessage>
																}
																<Paragraph className="leftMargin fontColor">{rule.defaultValue.length + ' / ' + maxInputCharachters}</Paragraph>
															</div>
														</div>
													)
												})
											}
										</>
									}

									<div className="NewReportOrder__BlockSpace--small">
										<Paragraph>
											{intl.formatMessage({id: 'invoice.invoiceComment'})}
										</Paragraph>
										<TextInput
											key={'invoiceReference'}
											id={'invoiceReference'}
											data-internal-id={'invoiceReference'}
											value={data.invoiceReference.value}
											maxLength={maxCharactersInvoiceReference}
											onChange={e => onChangeHandler(e)}
										/>
									
										<div className="CharacterCount">
											<Paragraph className="leftMargin fontColor">
												{data.invoiceReference.value.length + ' / ' + maxCharactersInvoiceReference}
											</Paragraph>
										</div>
									</div>
									
									<div className={'NewReportOrder__BlockSpace--big'}>
										<Heading4>
											{intl.formatMessage({ id: 'neworder.invoiceaddress' })}
										</Heading4>
										<div className={'InvoiceAddress'}>
											<p>{company.name}</p>
											<p>{companyAddress.address}</p>
											<p>{companyAddress.postalCode} {companyAddress.postalArea}</p>
											<p>{companyAddress.email}</p>
										</div>
									</div>
								</>
							}
							{
								company.costCenters.length <= 0 &&
								<InputErrorMessage>
									{intl.formatMessage({
										id: 'errormessage.no-invoice-address',
									})}
								</InputErrorMessage>
							}
							{clientViewStore.invalidNationalId && (
								<InputErrorMessage>
									{intl.formatMessage({
										id: 'errormessage.create-order-nationalId',
									})}
								</InputErrorMessage>
							)}
							{clientViewStore.createServerError && (
								<InputErrorMessage>
									{intl.formatMessage({ id: 'errormessage.create-order-server' })}
								</InputErrorMessage>
							)}
							<div className={'NewReportOrder__BlockSpace--big'}>
								<TextButton
									className={'NewReportOrder__BlockSpace--small'}
									disabled={disabled}
									onClick={() => setOrderConfirmationModal(true)}>
									{intl.formatMessage({ id: 'neworder.submit' })}
								</TextButton>
							</div>
							<div className={'NewReportOrder__BlockSpace--small'}>
								<Paragraph className={'NewReportOrder__BlockSpace--big'}>
									{intl.formatMessage(
										{ id: 'preamble.terms-of-delivery' },
										{
											termsCondition: (
												<Anchor
													href='/general-terms'
													target='_blank'>
													{intl.formatMessage({ id: 'common.terms-of-delivery' })}
													<LaunchIcon className='footer-links-launchIcon' fontSize='small' />
												</Anchor>
											),
										},
									)}
								</Paragraph>
							</div>
							{showTemporaryMessageOrderFormSubmit() &&
								<div style={{ textAlign: 'center', marginTop: '10px', fontWeight: 'bold', color: 'rgb(236, 157, 201)', marginBottom: '100px ' }}>
									{intl.formatMessage({ id: 'neworder.summerdelays.submit' })}
								</div>
							}
						</div>
					</Form>
					<OrderConfirmationModal open={orderConfirmationModal} onClose={toggleOrderConfirmationModal} onSubmit={onSubmitClick} formData={data} companyName={company.name} />
					{data.nationalId && <DuplicateOrderDetectedDialog open={confirmDuplicateOrderModal} onClose={toggleConfirmDuplicateOrderModal} nationalId={data.nationalId.value} />}
					<InvalidEmailDetectedDialog open={invalidEmailModal} onClose={toggleInvalidEmailModal} />
				</React.Fragment >
			}
		</>
	)
})

export default NewReportOrderForm
